import axios, { isAxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../index';
const { REACT_APP_API_URL } = process.env;

type ServerError = { error: string };

const createAsyncThunkWrapper = createAsyncThunk.withTypes<{
  rejectValue: string;
}>();

export type PayloadLogin = {
  email: string;
  password: string;
  remember?: string;
};

export type PayloadPasswordChange = {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
};

export type PayloadRegister = {
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  password: string;
  confirmPassword: string;
  city?: string;
  state?: string;
  zip?: string;
  agree: boolean;
};

export const loginUser = createAsyncThunkWrapper(
  'auth/loginUser',
  async (payload: PayloadLogin, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}/api/login`,
        payload
      );
      localStorage.setItem('userToken', response.data.token);
      localStorage.setItem('userInfo', JSON.stringify(response.data.user));
      return response;
    } catch (error) {
      if (isAxiosError<ServerError>(error) && error.response?.data.error) {
        return rejectWithValue(error.response?.data.error);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An error occured during the login process');
    }
  }
);

export const registerUser = createAsyncThunkWrapper(
  'auth/registerUser',
  async (payload: PayloadRegister, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}/api/signup`,
        payload
      );
      localStorage.setItem('userToken', response.data.token);
      localStorage.setItem('userInfo', JSON.stringify(response.data.user));
      return response;
    } catch (error) {
      if (isAxiosError<ServerError>(error) && error.response?.data.error) {
        return rejectWithValue(error.response?.data.error);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        'An error occured during the registration process'
      );
    }
  }
);

export const updateUserDetails = createAsyncThunkWrapper(
  'auth/updateUserDetails',
  async (payload: FormData, { rejectWithValue, getState }) => {
    try {
      const headers = { authorization: '' };
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.authorization = `Bearer ${token}`;
      }
      const response = await axios.put(
        `${REACT_APP_API_URL}/api/account/profile`,
        payload,
        { headers }
      );
      localStorage.setItem('userInfo', JSON.stringify(response.data));
      return response;
    } catch (error) {
      if (isAxiosError<ServerError>(error) && error.response?.data.error) {
        return rejectWithValue(error.response?.data.error);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An error occured during the updating process');
    }
  }
);

export const updateUserPassword = createAsyncThunkWrapper(
  'auth/updateUserPassword',
  async (payload: PayloadPasswordChange, { rejectWithValue, getState }) => {
    try {
      const headers = { authorization: '' };
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.authorization = `Bearer ${token}`;
      }
      const response = await axios.put(
        `${REACT_APP_API_URL}/api/account/password`,
        payload,
        { headers }
      );
      return response;
    } catch (error) {
      if (isAxiosError<ServerError>(error) && error.response?.data.error) {
        return rejectWithValue(error.response?.data.error);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An error occured during password updating');
    }
  }
);

export const switchTwoFactorAuth = createAsyncThunkWrapper(
  'auth/switchTwoFactorAuth',
  async (enable: boolean, { rejectWithValue, getState }) => {
    try {
      const headers = { authorization: '' };
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.authorization = `Bearer ${token}`;
      }
      const response = await axios.put(
        `${REACT_APP_API_URL}/api/account/two-factor`,
        { enable },
        { headers }
      );
      localStorage.setItem('userInfo', JSON.stringify(response.data));
      return response;
    } catch (error) {
      if (isAxiosError<ServerError>(error) && error.response?.data.error) {
        return rejectWithValue(error.response?.data.error);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        'An error occured during the switching two-factor auth'
      );
    }
  }
);
